import * as React from 'react';
import { MediaSet } from '@/types/media';

export interface DamFileIdentifier {
	readonly croppingId?: string | null;
	readonly fileId: string;
	readonly filePath?: string | null;
}

export interface UseDamFileHook {
	(fileIdentifier: null | DamFileIdentifier): null | MediaSet;
}

type DamFileIdentifierInput = DamFileIdentifier | string | null;

const isDamFileIdentifier = (value: unknown): value is DamFileIdentifier => {
	if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;

	const record = value as Record<string, unknown>;
	return (
		typeof record.fileId === 'string' &&
		record.fileId.trim().length > 0 &&
		(record.croppingId === undefined || record.croppingId === null || typeof record.croppingId === 'string') &&
		(record.filePath == null || typeof record.filePath === 'string')
	);
};

const normalizeDamFileIdentifier = (value: DamFileIdentifierInput): DamFileIdentifier | null => {
	if (value === null) return null;
	if (isDamFileIdentifier(value)) return value;

	const trimmedValue = value.trim();
	if (!(trimmedValue.startsWith('{') && trimmedValue.endsWith('}'))) {
		return null;
	}

	try {
		const parsedValue = JSON.parse(trimmedValue);
		return isDamFileIdentifier(parsedValue) ? parsedValue : null;
	} catch (_error) {
		return null;
	}
};

const UseDamFileHookContext = React.createContext<UseDamFileHook>(() => {
	console.error(`File was requested, but no instance of <DamFileProvider> was found.`);
	return null;
});

export interface DamFileProviderProps extends React.PropsWithChildren {
	readonly hook: UseDamFileHook;
}

export const DamFileProvider: React.FC<DamFileProviderProps> = ({ hook, children }) => {
	return <UseDamFileHookContext.Provider value={hook}>{children}</UseDamFileHookContext.Provider>;
};

export const useDamFile = (fileIdentifier: DamFileIdentifierInput): null | MediaSet => {
	const useDamFileHook = React.useContext(UseDamFileHookContext);
	return useDamFileHook(normalizeDamFileIdentifier(fileIdentifier));
};
